// src/utils/productUtils.js
// ─────────────────────────────────────────────────────────────────────────────
// Central product-behavior utilities for DawaCare.
// All type checks, discount math, and cart helpers live here so every
// component stays in sync with zero duplication.
// ─────────────────────────────────────────────────────────────────────────────

// ── Behaviour constants ───────────────────────────────────────────────────────
export const PRODUCT_BEHAVIOR = {
  MEDICINE: "medicine",  // Tablet, Capsule → packaging + pack/strip selling
  WEIGHT: "weight",    // Dairy     → weight / volume based
  STANDARD: "standard",  // Syrup, Cream, Drops, Inhaler, Sachet, Injection …
};

// Module-level dynamic map — SaleContext calls _updateCategoryBehaviorMap()
// whenever categories are loaded from Firebase, so all callers of
// getProductBehaviorType() automatically get dynamic behavior with zero
// changes to their call sites.
let _dynamicBehaviorMap = {};
export function _updateCategoryBehaviorMap(map) {
  _dynamicBehaviorMap = map || {};
}

export const MEDICINE_TYPES = ["Tablet", "Capsule"];
export const WEIGHT_TYPES = ["Dairy"];

export const TYPE_OPTIONS = [
  "Tablet", "Capsule", "Syrup", "Injection",
  "Cream", "Drops", "Sachet", "Inhaler", "Dairy",
];

/**
 * Returns the behaviour type for a given product.type string.
 * Checks (in order): explicit caller map → module-level Firebase map → hard-coded fallback.
 */
export function getProductBehaviorType(type = "", callerMap = {}) {
  const t = (type || "").trim();
  if (callerMap[t])          return callerMap[t];          // explicit override
  if (_dynamicBehaviorMap[t]) return _dynamicBehaviorMap[t]; // Firebase-loaded
  if (MEDICINE_TYPES.includes(t)) return PRODUCT_BEHAVIOR.MEDICINE;
  if (WEIGHT_TYPES.includes(t))   return PRODUCT_BEHAVIOR.WEIGHT;
  return PRODUCT_BEHAVIOR.STANDARD;
}

// ── Weight/volume quantity suggestions ────────────────────────────────────────
export const WEIGHT_QUANTITY_SUGGESTIONS = {
  Dairy: ["200ml", "500ml", "1 Liter", "1.5 Liter", "2 Liter", "200g", "400g", "1kg", "2kg"],
};

// ── Default empty structures (used by forms) ──────────────────────────────────
export const defaultPackaging = () => ({
  stripsPerPack: "",
  tabletsPerStrip: "",
});

export const defaultSellingOptions = () => ({
  pack: { enabled: true, label: "Full Pack", price: "", stock: "" },
  strip: { enabled: false, label: "Strip", price: "", stock: "" },
});

// ── Internal helper ───────────────────────────────────────────────────────────
function resolveDiscountPercent(product, discountLabel, sale, categoryDiscounts = {}) {
  // 1. Manual per-product discount (New field 'manualDiscount')
  if (product.manualDiscount !== undefined && product.manualDiscount !== null && product.manualDiscount !== "") {
    return parseFloat(product.manualDiscount);
  }

  // 2. Category Discount
  const productType = product.type || "";
  if (productType && categoryDiscounts[productType]) {
    return parseFloat(categoryDiscounts[productType]);
  }

  // Legacy manual mode check
  const isManual =
    product.discountMode === "manual" ||
    (product.discountMode === undefined &&
      product.discounts &&
      product.discounts !== discountLabel);

  if (isManual && product.discounts) {
    const match = product.discounts.match(/(\d+)/);
    if (match) return parseInt(match[1], 10);
  } else if (!isManual && sale?.effectiveIsActive && sale.discountPercent > 0) {
    return sale.discountPercent;
  }
  return null;
}

// ── Public pricing helpers ────────────────────────────────────────────────────

/**
 * Returns the discounted price for a product + selectedUnit, or null if none.
 * @param {object} product
 * @param {object} sale          from useSale()
 * @param {string} discountLabel from useSale()
 * @param {string|null} selectedUnit "pack" | "strip" | null
 */
export function getDiscountedPrice(product, sale, discountLabel, selectedUnit = null, categoryDiscounts = {}) {
  const behavior = getProductBehaviorType(product.type);
  let basePrice = null;

  if (behavior === PRODUCT_BEHAVIOR.MEDICINE && selectedUnit) {
    basePrice = product.sellingOptions?.[selectedUnit]?.price ?? null;
  } else {
    basePrice = product.retailPrice || null;
  }
  if (!basePrice) return null;

  const pct = resolveDiscountPercent(product, discountLabel, sale, categoryDiscounts);
  if (pct === null) return null;
  return Math.round(basePrice * (1 - pct / 100));
}

/** Returns the badge string e.g. "10% OFF", or null. */
export function getDiscountBadge(product, sale, discountLabel, categoryDiscounts = {}) {
  // 1. Manual per-product discount
  if (product.manualDiscount !== undefined && product.manualDiscount !== null && product.manualDiscount !== "") {
    return `${product.manualDiscount}% OFF`;
  }

  // 2. Category Discount
  const productType = product.type || "";
  if (productType && categoryDiscounts[productType]) {
    return `${categoryDiscounts[productType]}% OFF`;
  }

  // Legacy manual check
  const isManual =
    product.discountMode === "manual" ||
    (product.discountMode === undefined &&
      product.discounts &&
      product.discounts !== discountLabel);

  if (isManual && product.discounts) return product.discounts;
  if (!isManual && sale?.effectiveIsActive && sale.discountPercent > 0)
    return `${sale.discountPercent}% OFF`;
  return null;
}

/**
 * Returns the final effective unit price (after discount, if any).
 * Falls back gracefully for legacy products that have no sellingOptions.
 */
export function getEffectiveUnitPrice(product, sale, discountLabel, selectedUnit = null, categoryDiscounts = {}) {
  const discounted = getDiscountedPrice(product, sale, discountLabel, selectedUnit, categoryDiscounts);
  if (discounted !== null) return discounted;

  const behavior = getProductBehaviorType(product.type);
  if (behavior === PRODUCT_BEHAVIOR.MEDICINE && selectedUnit) {
    return product.sellingOptions?.[selectedUnit]?.price ?? product.price ?? 0;
  }
  return product.retailPrice || product.price || 0;
}

// ── Cart helpers ──────────────────────────────────────────────────────────────

/**
 * Returns the unique cart key:
 *   medicine → "<productId>_<unit>"  (pack & strip are separate cart entries)
 *   others   → "<productId>"
 */
export function getCartItemKey(productId, type, selectedUnit) {
  if (getProductBehaviorType(type) === PRODUCT_BEHAVIOR.MEDICINE && selectedUnit) {
    return `${productId}_${selectedUnit}`;
  }
  return productId;
}

/** Human-readable unit label, e.g. "Full Pack" or "Strip". */
export function getUnitLabel(product, selectedUnit) {
  if (!selectedUnit) return "";
  return product.sellingOptions?.[selectedUnit]?.label || selectedUnit;
}

/**
 * Returns the price to show on product CARDS (no unit selector there).
 * Medicine → cheapest enabled unit price. Others → retailPrice or price.
 */
export function getCardBasePrice(product) {
  const behavior = getProductBehaviorType(product.type);
  if (behavior === PRODUCT_BEHAVIOR.MEDICINE) {
    const opts = product.sellingOptions || {};
    for (const unit of ["pack", "strip"]) {
      if (opts[unit]?.enabled && opts[unit]?.price) return opts[unit].price;
    }
  }
  return product.retailPrice || product.price || 0;
}

/**
 * Returns the card display price — after discount, if applicable.
 * For medicine uses the first enabled unit.
 */
export function getCardDisplayPrice(product, sale, discountLabel, categoryDiscounts = {}) {
  const behavior = getProductBehaviorType(product.type);

  if (behavior === PRODUCT_BEHAVIOR.MEDICINE) {
    const firstUnit = getFirstEnabledUnit(product);
    if (!firstUnit) return "—";
    const discounted = getDiscountedPrice(product, sale, discountLabel, firstUnit, categoryDiscounts);
    return discounted ?? product.sellingOptions[firstUnit].price ?? 0;
  }

  const discounted = getDiscountedPrice(product, sale, discountLabel, null, categoryDiscounts);
  return discounted ?? product.retailPrice ?? product.price ?? 0;
}

/** First enabled selling unit key ("pack" | "strip" | null). */
export function getFirstEnabledUnit(product) {
  const opts = product.sellingOptions || {};
  for (const unit of ["pack", "strip"]) {
    if (opts[unit]?.enabled) return unit;
  }
  return null;
}
